import type { TimeInput } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { safeFormat } from './isomorphic/internal/safe-format';

/**
 * Format an instant in a given IANA zone with explicit `Intl` field options,
 * through the package's memoized `Intl.DateTimeFormat` cache.
 *
 * This is the low-level primitive for app-specific labels whose shape
 * {@link formatEventTime} doesn't cover — e.g. the bidder's "weekday · time"
 * close label or "Mon, May 25, 2026 at 1:00 PM" opening line. Routing those
 * per-row labels through this helper keeps every consumer on the shared cache
 * instead of constructing a fresh `Intl.DateTimeFormat` per call (the
 * "every consumer must memoize" drift the cache exists to prevent — these
 * formatters run per-row in lot grids and countdowns).
 *
 * An invalid zone falls back to the runtime's default zone (via `safeFormat`)
 * rather than throwing mid-render.
 *
 * @param instant ISO string, epoch ms, or Date.
 * @param zone IANA zone; omit to format in the runtime's default zone.
 * @param opts `Intl` date/time field options (month / day / weekday / hour / minute / …).
 * @returns the formatted string, or `''` for invalid input.
 * @sideEffect none — pure (the memoization is referentially transparent).
 */
export function formatInZone(
    instant: TimeInput,
    zone: string | undefined,
    opts: Intl.DateTimeFormatOptions,
): string {
    const d = toDate(instant);
    if (!d) return '';
    return safeFormat(d, zone ? { ...opts, timeZone: zone } : opts);
}
